// https://wiki.jtiweb.co.uk/display/DIGITECH/Date+format

import dayjs from "dayjs";
import debug from "debug";

import type { DateOfBirth } from "@jti/fe-common";
import { getConfig, getLocale } from "@jti/fe-common";

import { normalizeDate } from "./normalizeDate";

const log = debug("desjti:ui:formatDate");

const translateMonths = ({ date, format, locale }: { date: Date; format: string; locale: string }): string => {
  const monthLong = date.toLocaleString(locale, { month: "long" });
  const monthShort = date.toLocaleString(locale, { month: "short" });

  const longMonthRegex = /MMMM(?![^[]*])/g; // Matches MMMM if it's not surrounded by square brackets []
  const shortMonthRegex = /MMM(?![^[]*])/g; // Matches MMM if it's not surrounded by square brackets []
  format = format.replace(longMonthRegex, `[${monthLong}]`); // Add square brackets to ensure dayjs ignores it
  format = format.replace(shortMonthRegex, `[${monthShort}]`);

  return format;
};

export const translateWeekdays = ({ date, format, locale }: { date: Date; format: string; locale: string }): string => {
  const weekdayLong = date.toLocaleString(locale, { weekday: "long" });
  const weekdayShort = date.toLocaleString(locale, { weekday: "short" });
  const longWeekdayRegex = /dddd(?![^[]*])/g;
  const shortWeekdayRegex = /ddd(?![^[]*])/g;

  format = format.replace(longWeekdayRegex, `[${weekdayLong}]`);
  format = format.replace(shortWeekdayRegex, `[${weekdayShort}]`);

  return format;
};

// TODO: temporary fix for local to come from server side in next(serverSideLocale) to be fixed with DESP-96497
type FormatDate = (
  date: DateOfBirth | Date | undefined,
  format?: string,
  serverSideLocale?: string,
  treatAsLocalTime?: boolean
) => string;

export const formatDate: FormatDate = (date, format, serverSideLocale, treatAsLocalTime) => {
  if (!date) {
    return "";
  }

  const config = getConfig();
  const locale = serverSideLocale || getLocale();

  const customFormat = format || config?.dateFormat;

  const dateObject = normalizeDate(date, treatAsLocalTime);

  if (dateObject instanceof Date && isNaN(dateObject.getTime())) {
    if (typeof date === "string") {
      return date;
    }
    return "";
  }
  log("formatDate", {
    valueObtained: date,
    normalizedValue: dateObject,
    customFormat,
    locale,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  if (!customFormat) {
    return Intl.DateTimeFormat(locale).format(dateObject);
  }

  let formatWithTranslated = translateMonths({ date: dateObject, format: customFormat, locale });
  formatWithTranslated = translateWeekdays({ date: dateObject, format: formatWithTranslated, locale });
  return dayjs(dateObject).locale(locale).format(formatWithTranslated).replace(/\s+/g, " ");
};
